<script lang="ts" setup>
import type { UniqueLabel } from "@lxc/app-device-common";
import type { Ref } from "vue";
import { computed, ref } from "vue";
import { VALIDATION_NUMBER } from "~/core/constants/constants";
import LxcError from "~/core/models/LxcError.class";
import type { TruststoreForm } from "~/modules/certificate/models/TruststoreForm.interface";
import type { Option } from "~/types";
import ILxcAlertCircle from "~icons/lxc/alert-circle";

const props = defineProps<{
  disabled?: boolean;
  edition?: boolean;
  isTagsLoading: boolean;
  modelValue: TruststoreForm;
  tags?: string[] | null;
  tagsError?: LxcError | null;
}>();
const emit = defineEmits(["update:modelValue"]);

const { t } = useI18n();

const selectedTags = computed({
  get(): UniqueLabel[] {
    return props.modelValue.tags ?? [];
  },
  set(paramTags: UniqueLabel[]) {
    const tmpCertificate = props.modelValue;
    tmpCertificate.tags = paramTags;
    emit("update:modelValue", tmpCertificate);
  },
});

// deep copy of modelValue including subject property
const form = reactive<TruststoreForm>(props.modelValue);

let initTagOptionsPending = props.tags === undefined || !props.tags?.length;
const tagOptions: Ref<Option[]> = ref([]);

async function initTagOptions() {
  if (props.isTagsLoading) {
    initTagOptionsPending = true;
  } else if (!LxcError.check(props.tagsError)) {
    initTagOptionsPending = props.tags === undefined;

    tagOptions.value =
      props.tags?.map((tag: string) => {
        return {
          disabled:
            props.disabled ||
            !!selectedTags.value.find((selectedTag) => selectedTag.uid === tag),
          label: tag,
          value: tag,
        };
      }) ?? [];
  }
}

watch(
  () => form,
  (newForm: TruststoreForm) => {
    emit("update:modelValue", newForm);
  },
  {
    deep: true,
  },
);

watch(
  () => props.isTagsLoading,
  (isLoading) => {
    if (!isLoading && initTagOptionsPending) {
      initTagOptions();
    }
  },
);

const isTagError: ComputedRef<boolean> = computed(() => {
  return LxcError.check(props.tagsError);
});

onMounted(initTagOptions);
</script>

<template>
  <lxc-form-item v-if="modelValue">
    <lxc-label>{{ t("certificates.trustChain.label") }}</lxc-label>
    <certificate-chain
      :chain-links="modelValue.trustChain"
      :leaf-subject-alternative-names="modelValue.subjectAlternativeNames"
    />
  </lxc-form-item>

  <lxc-form-item :label="t('certificates.alias')" prop="alias">
    <lxc-input
      v-model="form.alias"
      type="text"
      :disabled="disabled || edition"
      :max-length="VALIDATION_NUMBER.ALIAS_MAX_LENGTH"
      required
    />
  </lxc-form-item>

  <lxc-alert v-if="isTagError" :icon="ILxcAlertCircle" type="error">
    <template #title>
      {{ t("certificates.tags.label") }}
      {{ t("certificates.tags.label") }}
    </template>
    <p>{{ tagsError?.toError()?.message ?? "" }}</p>
  </lxc-alert>

  <lxc-form-item :label="t('certificates.tags.label')" prop="tags">
    <lxc-tag-select
      v-model="selectedTags"
      :options="tagOptions"
      :placeholer="t('certificates.form.tags.placeholder')"
      :delete-tooltip="t('certificates.form.tags.remove.tooltip')"
      :disabled="disabled || isTagError"
    />
  </lxc-form-item>
</template>
